import React from 'react';

import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import PropTypes from 'prop-types';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import {
  Button,
  Container,
  Grid,
  InputLabel,
  OutlinedInput,
  Switch,
} from '@material-ui/core';
import { useSelector } from 'react-redux';

import ButtonsUseStyles from 'consts/styles/Buttons';
import CircleButton from 'components/common/components/CircleButton';
import Icons from 'consts/icons';
import { formStyles } from 'consts/styles/Form';
import { userStyles } from 'consts/styles/User';

import { useStyles } from './SystemSettingsUseStyles';

const AdminSettings = props => {
  const { save, valuesInit, settingTitle } = props;

  const [values, setValues] = React.useState(null);

  React.useEffect(() => {
    setValues(valuesInit);
  }, []);

  const listOfSettingsWithTopics = ['APS', 'AEROBITS', 'DRONES_SERVICE'];
  const [showPassword, setShowPassword] = React.useState(false);

  const subtitles = useSelector(
    state => state.language.adminZone.systemSettings
  );

  const classes = useStyles();
  const formClasses = formStyles();
  const classesUser = userStyles();
  const classesButtons = ButtonsUseStyles();

  const langSettingTitle = settingTitle.toLowerCase();

  const handleChange = e => {
    const { target } = e;
    const { name, value } = target;
    setValues({ ...values, [name]: value });
  };

  const deleteTopic = index => {
    if (values?.radars) {
      const updatedTopics = {};
      Object.keys(values?.radars).forEach((key, idx) => {
        if (idx !== index) updatedTopics[key] = values.radars[key];
      });
      return setValues({
        ...values,
        radars: updatedTopics,
      });
    }
    return setValues({
      ...values,
      topics: [...values.topics.filter((topic, idx) => index !== idx)],
    });
  };

  const addTopic = () => {
    if (!values.topic || values.topic === '') return;
    if (values?.radars) {
      const newIndex = `radar ${Object.keys(values.radars).length}`;
      return setValues({
        ...values,
        radars: { ...values.radars, [newIndex]: values.topic },
      });
    }
    return setValues({ ...values, topics: [...values.topics, values.topic] });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  const prepareDataToSave = () => ({
    external_urls: {
      zones_url: values.zones_url,
      zones_types_url: values.zones_types_url,
      ogn_models: values.ogn_models,
      open_sky_db: values.open_sky_db,
      open_sky_types: values.open_sky_types,
      missions_url: values.missions_url,
      weather_url: values.weather_url,
    },
    api_keys: {
      weather_api: values.weather_api,
    },
    email: {
      email_use_ssl: values.email_use_ssl,
      email_host: values.email_host,
      email_port: values.email_port,
      email_host_user: values.email_host_user,
      email_host_password: values.email_host_password,
      default_from_email: values.default_from_email,
    },
  });

  const saveSetting = () => {
    if (settingTitle === 'CORE') return save(settingTitle, prepareDataToSave());
    return save(settingTitle, values);
  };

  const handleTopics = () => {
    if (values?.topics || values?.radars) {
      let topic = 'topics';
      if (values?.radars) topic = 'radars';
      return (
        <>
          <Grid item xs={12}>
            <label className={`${formClasses.Form}__label`} htmlFor={topic}>
              {subtitles[langSettingTitle] &&
              subtitles[langSettingTitle][`${topic}_`]
                ? subtitles[langSettingTitle][`${topic}_`]
                : subtitles.common[`${topic}_`]}
              &nbsp;*
            </label>
          </Grid>
          {Object.values(values[topic]).map((item, index) => (
            <React.Fragment key={settingTitle + index}>
              <Grid container className={`${classes.SystemSettings}__topics`}>
                <Grid item xs={10}>
                  <OutlinedInput
                    name={`${topic}${index}`}
                    variant="outlined"
                    required
                    fullWidth
                    disabled
                    id={`${topic}${index}`}
                    defaultValue={item}
                    size="small"
                    classes={{
                      input: `${formClasses.Form}__text-field`,
                      focused: `${formClasses.Form}__text-field--focused`,
                    }}
                  />
                </Grid>
                <Grid
                  item
                  xs={2}
                  className={`${classes.SystemSettings}--text-align`}
                >
                  <Button
                    onClick={() => {
                      deleteTopic(index);
                    }}
                    className={`${classesButtons.Buttons} ${classesButtons.Buttons}--color ${classesButtons.ButtonsRectangle}`}
                  >
                    <Icons.Misc.Trash />
                  </Button>
                </Grid>
              </Grid>
            </React.Fragment>
          ))}
        </>
      );
    }
  };

  const handleSwitch = (e, v) => {
    const { target } = e;
    const { name } = target;
    setValues({
      ...values,
      [name]: typeof v === 'boolean' ? !v : !v.toString().toLowerCase(),
    });
  };

  return (
    <Container
      component="main"
      maxWidth={false}
      className={`${classesUser.User}__wrapper__content__container`}
    >
      <Grid
        container
        spacing={2}
        className={`${formClasses.Form}__overflow-component`}
      >
        {values &&
          Object.keys(values).map(
            (item, index) =>
              typeof values[item] !== 'object' &&
              item !== 'topic' && (
                <React.Fragment key={settingTitle + index}>
                  <Grid item xs={12} key={`grid${index}`}>
                    <InputLabel
                      htmlFor={item}
                      key={`label${index}`}
                      className={`${formClasses.Form}__label`}
                    >
                      {subtitles[langSettingTitle] &&
                      subtitles[langSettingTitle][`${item}_`]
                        ? subtitles[langSettingTitle][`${item}_`]
                        : subtitles.common[`${item}_`]}
                      &nbsp;*
                    </InputLabel>
                    {values[item] !== undefined &&
                    (values[item].toString().toLowerCase() === 'true' ||
                      values[item].toString().toLowerCase() === 'false') ? (
                      <>
                        <Switch
                          checked={
                            typeof values[item] === 'boolean'
                              ? values[item]
                              : values[item].toString().toLowerCase() === 'true'
                          }
                          onChange={e => handleSwitch(e, values[item])}
                          color="primary"
                          name={item}
                          inputProps={{ 'aria-label': 'primary checkbox' }}
                          classes={{
                            checked: `${formClasses.Form}__switch`,
                            track: `${formClasses.Form}__switch__track`,
                          }}
                        />
                      </>
                    ) : (
                      <OutlinedInput
                        key={`textField${index}`}
                        name={item}
                        type={
                          item.includes('pass') && !showPassword
                            ? 'password'
                            : 'text'
                        }
                        variant="outlined"
                        required
                        fullWidth
                        id={item}
                        onChange={handleChange}
                        defaultValue={
                          values[item] !== undefined ? values[item] : ''
                        }
                        classes={{
                          input: `${formClasses.Form}__text-field`,
                          focused: `${formClasses.Form}__text-field--focused`,
                        }}
                        size="small"
                        endAdornment={
                          item.includes('pass') && (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge="end"
                              >
                                {showPassword ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          )
                        }
                      />
                    )}
                  </Grid>
                </React.Fragment>
              )
          )}
        {handleTopics()}
        {listOfSettingsWithTopics.includes(settingTitle) && (
          <>
            <Grid item xs={12}>
              <label className={`${formClasses.Form}__label`} htmlFor="topic">
                {subtitles[langSettingTitle] &&
                subtitles[langSettingTitle].add_topic_
                  ? subtitles[langSettingTitle].add_topic_
                  : subtitles.common.add_topic_}
                &nbsp;*
              </label>
            </Grid>
            <Grid item xs={11}>
              <OutlinedInput
                name="topic"
                variant="outlined"
                required
                fullWidth
                onChange={handleChange}
                id="topic"
                type="text"
                defaultValue={values?.topic}
                classes={{
                  input: `${formClasses.Form}__text-field`,
                  focused: `${formClasses.Form}__text-field--focused`,
                }}
                size="small"
              />
            </Grid>

            <Grid
              item
              xs={1}
              className={`${classes.SystemSettings}__button__add`}
            >
              <CircleButton
                onClickFunction={addTopic}
                PassedIcon="+"
                size={3}
              />
            </Grid>
          </>
        )}
        <Button
          onClick={() => saveSetting()}
          type="button"
          fullWidth
          className={`${classesButtons.Buttons} ${classesButtons.Buttons}--color ${classesButtons.ButtonsRectangle}`}
        >
          {subtitles.buttonText_}
        </Button>
      </Grid>
    </Container>
  );
};

AdminSettings.propTypes = {
  save: PropTypes.func.isRequired,
  valuesInit: PropTypes.object,
  settingTitle: PropTypes.string,
};

export default AdminSettings;
